import React, { useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { Avatar, Badge, Button, Empty } from "antd";
import {
  ArrowLeftOutlined,
  BellOutlined,
  CheckOutlined,
  CommentOutlined,
  HomeOutlined,
  LikeOutlined,
  LoginOutlined,
  TeamOutlined,
  UserAddOutlined,
  UserOutlined,
} from "@ant-design/icons";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-toastify";
import useUser from "../../hooks/useUser";
import { useSession } from "../../lib/session";
import Sheet from "./Sheet";
import "./app-shell.css";

export const BloodDropIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" width="1em" height="1em" aria-hidden="true">
    <path
      fill="currentColor"
      d="M12 2.5c-.3 0-.6.15-.78.4C9.6 5.1 5 11.3 5 15a7 7 0 0 0 14 0c0-3.7-4.6-9.9-6.22-12.1A.98.98 0 0 0 12 2.5Zm-3.2 12.2a.9.9 0 0 1 .9.9 2.3 2.3 0 0 0 2.3 2.3.9.9 0 0 1 0 1.8 4.1 4.1 0 0 1-4.1-4.1.9.9 0 0 1 .9-.9Z"
    />
  </svg>
);

const formatDate = (value) =>
  new Date(value).toLocaleString([], {
    month: "short",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });

const Tab = ({ to, icon, label, badge = 0, end }) => (
  <NavLink to={to} end={end} className={({ isActive }) => `app-tab ${isActive ? "app-tab--active" : ""}`}>
    <Badge count={badge} size="small" offset={[4, 0]}>
      <span className="app-tab__icon">{icon}</span>
    </Badge>
    <span className="app-tab__label">{label}</span>
  </NavLink>
);

const NotificationsSheet = ({ open, onClose, notifications }) => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { markAllNotificationsRead } = useUser();

  const { mutate: markAllRead, isPending: markingRead } = useMutation({
    mutationFn: markAllNotificationsRead,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["member-notifications"] }),
    onError: () => toast.error("Failed to mark notifications as read"),
  });

  return (
    <Sheet open={open} onClose={onClose} height="80%">
      <div className="notif-head">
        <h2 className="sheet-form__title">Notifications</h2>
        <Button size="small" icon={<CheckOutlined />} loading={markingRead} onClick={() => markAllRead()}>
          Mark all read
        </Button>
      </div>
      {notifications.length === 0 ? (
        <Empty description="No notifications yet" />
      ) : (
        <div className="notif-list">
          {notifications.map((item) => {
            const actor = item?.actorUserId || {};
            const post = item?.postId || {};
            const isComment = item?.type === "comment";
            const postId = post?._id || item?.postId;
            return (
              <button
                type="button"
                key={item._id}
                className={`notif ${item?.isRead ? "" : "notif--unread"}`}
                onClick={() => {
                  if (postId) navigate(`/post/${postId}`);
                  onClose();
                }}
              >
                <Avatar size={36} src={actor?.profilePhoto || undefined} icon={<UserOutlined />} />
                <span className="notif__body">
                  <span className="notif__text">
                    <strong>{actor?.name || "A member"}</strong>{" "}
                    {isComment ? "commented on your post" : "liked your post"}{" "}
                    {isComment ? <CommentOutlined /> : <LikeOutlined />}
                  </span>
                  {isComment && item?.commentText ? (
                    <span className="notif__quote">"{item.commentText}"</span>
                  ) : null}
                  <span className="notif__time">{formatDate(item.createdAt)}</span>
                </span>
              </button>
            );
          })}
        </div>
      )}
    </Sheet>
  );
};

// The mobile-app frame every member and public screen renders inside:
// a top app bar, the page content, and a bottom tab bar.
//   title    - app bar title; the 0709 brand shows when omitted
//   back     - show a back arrow; a path string goes there, `true` goes back in history
//   actions  - extra app bar buttons on the right
//   footer   - pinned above the tab bar (e.g. a chat composer)
//   hideTabs - full-screen pages such as a chat thread
//   flush    - no padding around the content
const AppShell = ({ title, subtitle, back, actions, footer, hideTabs = false, flush = false, children }) => {
  const navigate = useNavigate();
  const { endUser, openLogin } = useSession();
  const { getMyNotifications, getConversations } = useUser();
  const [notificationsOpen, setNotificationsOpen] = useState(false);
  const isMember = !!endUser;

  const { data: notificationData } = useQuery({
    queryKey: ["member-notifications"],
    queryFn: () => getMyNotifications({ limit: 40 }),
    enabled: isMember,
    refetchOnWindowFocus: true,
    refetchInterval: 30000,
  });

  const { data: conversations = [] } = useQuery({
    queryKey: ["chat-conversations"],
    queryFn: () => getConversations({ page: 1, limit: 50 }),
    enabled: isMember,
    refetchInterval: 30000,
  });

  const unreadNotifications = notificationData?.unreadCount || 0;
  const unreadChats = conversations.reduce((sum, conv) => sum + (conv?.unreadCount || 0), 0);

  const goBack = () => {
    if (typeof back === "string") navigate(back);
    else if (window.history.length > 1) navigate(-1);
    else navigate("/");
  };

  return (
    <div className="app-shell">
      <header className="app-bar">
        {back ? (
          <button type="button" className="app-bar__icon-btn" aria-label="Back" onClick={goBack}>
            <ArrowLeftOutlined />
          </button>
        ) : null}

        <div className="app-bar__title">
          {title ? (
            <>
              <span className="app-bar__heading">{title}</span>
              {subtitle ? <span className="app-bar__subtitle">{subtitle}</span> : null}
            </>
          ) : (
            <button type="button" className="app-brand" onClick={() => navigate(isMember ? "/feed" : "/")}>
              <span className="app-brand__mark">0709</span>
              <span className="app-brand__name">Bangladesh</span>
            </button>
          )}
        </div>

        <div className="app-bar__actions">
          {actions}
          {isMember ? (
            <button
              type="button"
              className="app-bar__icon-btn"
              aria-label="Notifications"
              onClick={() => setNotificationsOpen(true)}
            >
              <Badge count={unreadNotifications} size="small">
                <BellOutlined />
              </Badge>
            </button>
          ) : null}
        </div>
      </header>

      <main className={`app-content ${flush ? "app-content--flush" : ""}`}>{children}</main>

      {footer || !hideTabs ? (
        <div className="app-bottom">
          {footer}
          {!hideTabs && (
            <nav className="app-tabs" aria-label="Main">
              {isMember ? (
                <>
                  <Tab to="/feed" icon={<HomeOutlined />} label="Feed" />
                  <Tab to="/blood" icon={<BloodDropIcon />} label="Find Blood" />
                  <Tab to="/messages" icon={<CommentOutlined />} label="Chats" badge={unreadChats} />
                  <Tab to="/members" icon={<TeamOutlined />} label="Members" />
                  <Tab to="/my-profile" icon={<UserOutlined />} label="Profile" />
                </>
              ) : (
                <>
                  <Tab to="/" end icon={<HomeOutlined />} label="Home" />
                  <Tab to="/blood" icon={<BloodDropIcon />} label="Find Blood" />
                  <Tab to="/app-registration" icon={<UserAddOutlined />} label="Join" />
                  <button type="button" className="app-tab" onClick={openLogin}>
                    <span className="app-tab__icon">
                      <LoginOutlined />
                    </span>
                    <span className="app-tab__label">Log in</span>
                  </button>
                </>
              )}
            </nav>
          )}
        </div>
      ) : null}

      {isMember ? (
        <NotificationsSheet
          open={notificationsOpen}
          onClose={() => setNotificationsOpen(false)}
          notifications={notificationData?.notifications || []}
        />
      ) : null}
    </div>
  );
};

export default AppShell;
